import { FiUser } from 'react-icons/fi';
import { cn } from '../../utils/helpers';

/**
 * Member avatar — photo when there is one, tinted initial when there is not.
 *
 * The fallback tint is derived from the member id (or the name, when no id is
 * to hand) so the same person keeps the same colour everywhere in the panel.
 * All five tints come from the site palette, so a conversation full of
 * initials still reads as this site rather than as a random colour wheel.
 */

const TONES = [
  'bg-primary text-white',
  'bg-secondary text-white',
  'bg-secondary-600 text-white',
  'bg-primary-400 text-white',
  'bg-primary-600 text-white',
];

const SIZES = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-7 h-7 text-[11px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
};

function toneFor(seed) {
  const key = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 9973;
  return TONES[hash % TONES.length];
}

export default function Avatar({ name, photoUrl, seed, size = 'md', variant = 'auto', className }) {
  const dims = SIZES[size] || SIZES.md;
  const initial = name?.trim()?.[0]?.toUpperCase();
  // `light` is the conversation tile on the white title bar, where a hashed
  // navy circle would fight the bar rather than sit on it.
  const tone = variant === 'light' ? 'bg-highlight text-primary' : toneFor(seed ?? name);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        loading="lazy"
        className={cn('rounded-full object-cover shrink-0 ring-1 ring-primary/10', dims, className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'rounded-full shrink-0 flex items-center justify-center font-semibold leading-none',
        variant === 'light' ? 'ring-1 ring-primary/10' : 'ring-1 ring-white/20',
        dims,
        tone,
        className
      )}
    >
      {initial || <FiUser className="w-3.5 h-3.5" />}
    </span>
  );
}
